import { either, isNil, isEmpty, not, pathOr } from 'ramda'

const checkFilter = filter => not(either(isNil, isEmpty)(filter))

const baseUrl = process.env.API_URL + '/api'

const signalR = {
  connection () {
    return baseUrl.replace(/\/api$/, '')
  }
}

const publicSettings = {
  getPublicSettings () {
    return `/public/settings`
  }
}

const formConstuctor = {
  slug: '/backoffice',
  update () {
    return `${this.slug}/general-settings/form-constructor`
  }
}

const mapLocations = {
  slug: '/backoffice/map-location',
  get () {
    return `${this.slug}`
  },
  add () {
    return `${this.slug}`
  },
  delete (id) {
    return `${this.slug}/${id}`
  }
}

const form = {
  slug: '/public/forms',
  getFormByType (type) {
    return `${this.slug}/${type}`
  }
}

const auth = {
  slug: '/backoffice/login',
  login () {
    return `${this.slug}`
  },
  setPassword: () => '/backoffice/forgot-password',
  sendSetPasswordLink: () => '/backoffice/forgot-password/request',
  refreshToken: () => `backoffice/refresh-token`
}

const users = {
  getUsersList (userName, email) {
    if (email || userName) {
      // TODO need add regular expression FE-2757
      const filtrationName = `filter.UserName.Like=/${encodeURIComponent(userName)}/i`
      const filtrationEmail = `&filter.Email.Like=/${encodeURIComponent(email)}/i`
      return `/backoffice/master/users?${filtrationName}${filtrationEmail}`
    } else {
      return `/backoffice/master/users`
    }
  },
  getUsersListForSuperAdmin (userName, email) {
    if (email || userName) {
      // TODO need add regular expression FE-2757
      const filtrationName = `filter.UserName.Like=/${encodeURIComponent(userName)}/i`
      const filtrationEmail = `&filter.Email.Like=/${encodeURIComponent(email)}/i`
      return `/backoffice/super-admin/users?${filtrationName}${filtrationEmail}`
    } else {
      return `/backoffice/super-admin/users`
    }
  },
  getUsersActionsTypes () {
    return '/backoffice/super-admin/users/action-logs/actions'
  },
  getUsersActions ({page = 0, itemsOnPage = 25, userName, email, type} = {}) {
    if (userName || email || type) {
      const filtrationEmail = email ? `&filter.User.Email.Like=/${encodeURIComponent(email)}/i` : ''
      const filtrationName = userName ? `&filter.User.UserName.Like=/${encodeURIComponent(userName)}/i` : ''
      const filterType = type ? `&filter.Action.equals=${encodeURIComponent(type)}` : ''
      return `/BackofficeUserActionLogs?sort.Desc=ActionDate&page=${page}&itemsOnPage=${itemsOnPage}${filtrationName}${filtrationEmail}${filterType}`
    } else {
      return `/BackofficeUserActionLogs?sort.Desc=ActionDate&page=${page}&itemsOnPage=${itemsOnPage}`
    }
  },
  req: (id) => `/backoffice/master/users?id=${id}`,
  reqBySuperAdmin: (id) => `/backoffice/super-admin/users?id=${id}`,
  post: () => '/backoffice/master/users',
  scopes: () => '/meta/scopes',
  logoutAdmin () {
    return '/backoffice/super-admin/users/logout'
  },
  logoutAdmins: () => '/backoffice/super-admin/users/logout/all-online',
  getDetails: () => '/backoffice/user/current/info'
}

const video = {
  slug: '/backoffice/general-settings/video',
  req () {
    return this.slug
  },
  delete (videoId) {
    return `${this.slug}/${videoId}`
  }
}

const videosDisplay = {
  update () {
    return `/backoffice/general-settings/video-gallery`
  }
}

const banner = {
  slug: '/public/banners',
  settingsSlug: '/backoffice/general-settings/promotional-image',
  req () {
    return this.slug
  },
  create () {
    return `${this.settingsSlug}`
  },
  getBannersList ({ page = 0, itemsOnPage = 9999, filter, sort, name }) {
    const sorting = checkFilter(sort) ? `&sort.${sort}=${name}` : ''
    // TODO need add regular expression FE-2757
    const filtration = checkFilter(filter) ? `&filter.Metadata.description.Like=/${encodeURIComponent(filter)}/i` : ''
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}${filtration}${sorting}`
  },
  delete (bannerId) {
    return `${this.settingsSlug}/delete/${bannerId}`
  },
  fetch () {
    return this.slug
  },
  update () {
    return `${this.settingsSlug}`
  },
  savePageWithBanner () {
    return `${this.settingsSlug}/settings`
  }
}

const invitation = {
  slug: '/backoffice/blitz/invite',
  getSingleLetterLink () {
    return this.slug
  }
}

const hideOrShowEnvelop = {
  slug: '/backoffice/blitz/settings',
  updateHideOrShowEnvelop () {
    return this.slug
  }
}

const invitationSingle = {
  getDinamicLetterLink (way) {
    return `/backoffice/${way}/invite`
  }
}

const blockedIps = {
  getBlockedIps ({ itemsOnPage = 9999, page = 0 } = {}) {
    return `/backoffice/super-admin/blockedIp?itemsOnPage=${itemsOnPage}&page=${page}`
  },
  addBlockedIp () {
    return `/backoffice/super-admin/blockedIp`
  },
  deleteBlockedIp (id) {
    return `/backoffice/super-admin/blockedIp/${id}`
  }
}

const invitationMultiply = {
  getMultiplyBlitzLink () {
    return `/backoffice/blitz/invite/multiple`
  },
  getMultiplyListingLink () {
    return `/backoffice/listing/invite/multiple`
  },
  getMultiplyUploadAttendeeLink () {
    return '/backoffice/registration/last-year-attendee'
  }
}

const vipInvitation = {
  slug: '/backoffice/registration/attendee/vip/invite',
  reqSingle () {
    return this.slug
  },
  reqMultiple () {
    return `${this.slug}/multiple`
  },
  sendBatchedItemsTable () {
    return '/backoffice/registration/attendee/vip/parse-excel-groups'
  }
}

const packages = {
  slug: '/backoffice/registration/package',
  getPackagesList ({ page = 0, itemsOnPage = 9999, id } = {}) {
    let filterParams = id ? `&filter.Id.Equals=${id}` : ''
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}${filterParams}`
  },
  addPackageList () {
    return `${this.slug}`
  },
  editPackageList () {
    return `${this.slug}`
  },
  deletePackageList (id) {
    return `${this.slug}?id=${id}`
  },
  editKillerFeatureData () {
    return `/backoffice/registration/killer-feature/display-settings`
  }
}

const predefinedActivity = {
  slug: '/backoffice/registration/predefined-activities',
  getPredefinedActivity (conf = { page: 0, itemsOnPage: 10 }) {
    const { page, itemsOnPage } = conf
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}`
  },
  addPredefinedActivity () {
    return `${this.slug}`
  },
  editPredefinedActivity () {
    return `${this.slug}`
  },
  deletePredefinedActivity (id) {
    return `${this.slug}/?id=${id}`
  }
}

const blitz = {
  slug: '/backoffice/blitz',
  submits ({ page = 0, itemsOnPage = 9999, filter, sorter, equalsStatus, equalsCategory, equalsVisibilityStatus }) {
    const sorting = checkFilter(sorter.sortBy) ? `&sort.${sorter.direction}=${sorter.sortBy}` : ''
    // TODO need add regular expression FE-2757
    const filtration = checkFilter(filter.searchValue) ? `&filter.${filter.filterBy}.Like=/${encodeURIComponent(filter.searchValue)}/i` : ''
    const status = checkFilter(equalsStatus.status) ? `&filter.${equalsStatus.filterBy}.equals=${equalsStatus.status}` : ''
    const category = checkFilter(equalsCategory.category) ? `&filter.${equalsCategory.filterBy}.equals=${equalsCategory.category}` : ''
    const visibilityStatus = checkFilter(equalsVisibilityStatus.visibilityStatus) ? `&filter.BlitzCard.Status.equals=${equalsVisibilityStatus.visibilityStatus}` : ''
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}&sort.Desc=Id${filtration}${sorting}${status}${category}${visibilityStatus}`
  },
  counterBlitz () {
    return `${this.slug}/counter`
  },
  createBlitz () {
    return `${this.slug}`
  },
  updateStatus (actionString, submitId) {
    return `${this.slug}/${actionString}?submitId=${submitId}`
  },
  remind () {
    return `${this.slug}/remind`
  }
}

const agenda = {
  locationsSlug: '/backoffice/agenda/locations',
  settingsSlug: '/backoffice/agenda/settings',
  sessionsSlug: '/backoffice/agenda/sessions',
  getLocationsList ({ page = 0, itemsOnPage = 10, filter, sort, name, fetchAll = false }) {
    if (fetchAll) {
      return `${this.locationsSlug}?page=${page}&itemsOnPage=99999`
    } else {
      const sorting = checkFilter(sort) ? `&sort.${sort}=${name}` : ''
      // TODO need add regular expression FE-2757
      const filtration = checkFilter(filter) ? `&filter.Name.Like=/${encodeURIComponent(filter)}/i` : ''
      return `${this.locationsSlug}?page=${page}&itemsOnPage=${itemsOnPage}${sorting}${filtration}`
    }
  },
  setPublishedStatus () {
    return `${this.sessionsSlug}/status`
  },
  addLocationList () {
    return `${this.locationsSlug}`
  },
  removeLocation (id) {
    return `${this.locationsSlug}/${id}`
  },
  editSettings () {
    return `${this.settingsSlug}`
  },
  createSession () {
    return `${this.sessionsSlug}`
  },
  editSession () {
    return `${this.sessionsSlug}`
  },
  removeSession (id) {
    return `${this.sessionsSlug}/${id}`
  },
  getAgendaSessions ({ page = 0, itemsOnPage = 9999, filter, sortByLocation, sort, name, fetchAll = false }) {
    if (fetchAll) {
      return `${this.sessionsSlug}?page=${page}&itemsOnPage=99999`
    } else {
      const sorting = checkFilter(sort) && name ? `&sort.${sort}=${name}` : ''
      const filtrationTitle = checkFilter(filter.filterTitle) ? `&filter.Title.Like=/${filter.filterTitle}/i` : ''
      const filtrationDescription = checkFilter(filter.filterDescription) ? `&filter.ShortDescription.Like=/${encodeURIComponent(filter.filterDescription)}/i` : ''
      const filtrationType = checkFilter(filter.filterType) ? `&filter.Metadata.sessionType.equals=${filter.filterType}` : ''
      const location = checkFilter(sortByLocation) ? `&filter.Location.Id.equals=${sortByLocation}` : ''
      const filtrationSessionType = checkFilter(filter.sessionType) ? `&filter.Metadata.sessionType.Equals=${filter.sessionType}` : ''
      return `${this.sessionsSlug}?page=${page}&itemsOnPage=${itemsOnPage}${sorting}${filtrationTitle}${filtrationType}${filtrationDescription}${location}${filtrationSessionType}`
    }
  }
}

// const sponsorsTasks = {
//   slug: '/admin/sponsor-tasks',
//   getSponsorsTasks () {
//     return `${this.slug}`
//   }
// }
//
// const sponsorsTypes = {
//   slug: '/admin/sponsor-types',
//   getSponsorsTypes () {
//     return `${this.slug}`
//   }
// }
//
// const sponsorsTiers = {
//   slug: '/admin/sponsor-tiers',
//   getSponsorsTiers () {
//     return `${this.slug}`
//   }
// }

const sponsors = {
  slug: '/backoffice/media-partners',
  typesSlug: '/backoffice/media-partners/types',
  tasksSlug: '/backoffice/media-partners/tasks',
  tasksSlugNormal: '/backoffice/media-partners-tasks',
  tiersSlug: '/backoffice/attendee-type',
  mediaKitsSlug: '/backoffice/media-kits',
  sendGrid: '/backoffice/general-settings/send-grid',
  getSponsors ({ page = 0, itemsOnPage = 10, search, sort = 'Asc', name = 'Status', fieldName, fieldVal, operatorName, filter }) {
    const sortingParams = checkFilter(name) ? `&sort.${sort}=${name}` : ''
    let filterParams = ''
    let isRegExpNotPossible = !(operatorName === 'Like')
    if (filter) {
      filterParams = isRegExpNotPossible ? `&filter.${fieldName}.${operatorName}=${fieldVal}` : `&filter.${fieldName}.${operatorName}=/${fieldVal}/i`
    } else if (fieldName && fieldVal && operatorName) {
      filterParams = isRegExpNotPossible ? `&filter.${fieldName}.${operatorName}=${fieldVal}` : `&filter.${fieldName}.${operatorName}=/${fieldVal}/i`
    } else {
      filterParams += search.type ? `&filter.SponsorTypes.Any=${encodeURIComponent(search.type)}` : ''
      filterParams += search.tier ? `&filter.SponsorTiers.Any=${encodeURIComponent(search.tier)}` : ''
      // TODO need add regular expression FE-2757
      filterParams += search.company ? `&filter.Company.Like=/${encodeURIComponent(search.company)}/i` : ''
    }
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}${sortingParams}${filterParams}`
  },
  createSponsor () {
    return `${this.slug}`
  },
  editSponsor () {
    return `${this.slug}`
  },
  deleteSponsor (ids) {
    // id can be single or array of ids
    const queryStr = Array.isArray(ids)
      ? ids.reduce((acc, el, index) => {
        acc += `${index === 0 ? '' : '&'}command.sponsorsIds=${el}`
      }, '')
      : `command.sponsorsIds=${ids}`
    return `${this.slug}?${queryStr}`
  },
  createSponsorType () {
    return `${this.typesSlug}`
  },
  updateSponsorPublish () {
    return `${this.slug}/publish`
  },
  updateSponsorDraft () {
    return `${this.slug}/draft`
  },
  updateSponsorTask () {
    return `${this.slug}/tasks`
  },
  getSponsorTypes ({ page = 0, itemsOnPage = 100, filter, sort, name }) {
    const sorting = checkFilter(sort) ? `&sort.${sort}=${name}` : ''
    // TODO need add regular expression FE-2757
    const filtration = checkFilter(filter) ? `&filter.Title.Like=/${filter}/i` : ''
    return `${this.typesSlug}?page=${page}&itemsOnPage=${itemsOnPage}${sorting}${filtration}`
  },
  editSponsorType () {
    return `${this.typesSlug}`
  },
  deleteSponsorType (id) {
    return `${this.typesSlug}?command.id=${id}`
  },
  getSponsorTasks ({ page = 0, itemsOnPage = 100, filter, sort, name }) {
    const sorting = checkFilter(sort) ? `&sort.${sort}=${name}` : ''
    // TODO need add regular expression FE-2757
    const filtration = checkFilter(filter) ? `&filter.Title.Like=/${filter}/i` : ''
    return `${this.tasksSlugNormal}?page=${page}&itemsOnPage=${itemsOnPage}${sorting}${filtration}`
  },
  createSponsorTask () {
    return `${this.tasksSlugNormal}`
  },
  editSponsorTask () {
    return `${this.tasksSlugNormal}`
  },
  deleteSponsorTask (id) {
    return `${this.tasksSlugNormal}?command.id=${id}`
  },
  sendReminderWithSponsorTasks () {
    return `${this.tasksSlug}/remind`
  },
  recieveEmailTemplates () {
    return `${this.sendGrid}`
  },
  createSponsorTier () {
    return `${this.tiersSlug}`
  },
  editSponsorTier () {
    return `${this.tiersSlug}`
  },
  deleteSponsorTier (id) {
    // return `${this.tiersSlug}?command.id=${id}`
    return `${this.tiersSlug}?id=${id}`
  },
  getSponsorTiers ({ page = 0, itemsOnPage = 100, filter, sort, name }) {
    const sorting = checkFilter(sort) ? `&sort.${sort}=${name}` : ''
    // TODO need add regular expression FE-2757
    const filtration = checkFilter(filter) ? `&filter.Title.Like=/${filter}/i` : ''
    return `${this.tiersSlug}?page=${page}&itemsOnPage=${itemsOnPage}${sorting}${filtration}`
  },
  setSettings () {
    return '/backoffice/media-partners/settings'
  },
  getMediaKits () {
    return `${this.mediaKitsSlug}`
  },
  createMediaKit () {
    return `${this.mediaKitsSlug}`
  },
  updateMediaKit () {
    return `${this.mediaKitsSlug}`
  },
  deleteMediaKit (id) {
    return `${this.mediaKitsSlug}?id=${id}`
  },
  updateMediaKitsSettings () {
    return `${this.mediaKitsSlug}/settings`
  }
}

const galleries = {
  get: ({ page, itemsOnPage, category, title }) => {
    // TODO need add regular expression FE-2757
    const filterParams = checkFilter(title) ? `&filter.Title.Like=/${encodeURIComponent(title)}/i` : ''
    const filterCategory = checkFilter(category) ? `&filter.Categories.Like=${category}` : ''
    return `backoffice/general-settings/gallery?page=${page}&itemsOnPage=${itemsOnPage}${filterParams}${filterCategory}`
  },
  req: () => '/backoffice/general-settings/gallery',
  postImage: () => '/backoffice/general-settings/images-storage/upload',
  getImgUrl (imgId) {
    return `public/gallery?query.id=${imgId}`
  },
  deleteImage (ids) {
    return `/backoffice/general-settings/gallery/${ids}`
  },
  deleteStorageImage (cloudinaryIds) {
    return `/backoffice/general-settings/images-storage?model.imagesIds=${cloudinaryIds}`
  }
}

const invitations = {
  slug: '/public',
  blitz (token) {
    return `${this.slug}/blitz/submit?token=${token}`
  }
}

const categories = {
  slug: '/backoffice/master/categories',
  slugPublic: '/public/categories',
  getCategoriesList ({ page = 0, itemsOnPage = 100, filter, sort, name }) {
    const id = pathOr('', ['id'], filter)
    const categoryName = pathOr('', ['categoryName'], filter)
    const sortingParams = checkFilter(name) ? `&sort.${sort}=${name}` : ''
    let filterParams = ''
    if (id || categoryName) {
      filterParams = id ? `&filter.Id.Equals=${id}` : ''
      // TODO need add regular expression FE-2757
      filterParams += categoryName ? `&filter.Name.Like=/${encodeURIComponent(categoryName)}/i` : ''
    } else {
      filterParams = checkFilter(filter) ? `&filter.Name.Like=${encodeURIComponent(filter)}` : ''
    }
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}${sortingParams}${filterParams}`
  },
  addCategoryList () {
    return `${this.slug}`
  },
  editCategoryList () {
    return `${this.slug}`
  },
  deleteCategoryList (id) {
    return `${this.slug}/${id}`
  },
  getEnabledCategoriesList () {
    return `${this.slug}?itemsOnPage=999&filter.Status.Equals=Enabled`
  }
}

const vipAttendeeType = {
  slug: '/backoffice/attendee-type',
  get ({ page = 0, itemsOnPage = 9999, sorts, filters } = {}) {
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}`
  },
  addVipAttendeeTypes () {
    return `${this.slug}`
  },
  editVipAttendeeTypes () {
    return `${this.slug}`
  },
  deleteVipAttendeeTypes (id) {
    return `${this.slug}/?id=${id}`
  }
}

const blackList = {
  slug: '/backoffice/blacklist',
  get () {
    return `${this.slug}`
  },
  addToBlacklist (tag) {
    return `${this.slug}?tag=${tag}`
  },
  deleteFromBlacklist (id) {
    return `${this.slug}/${id}`
  }
}

const attendee = {
  slug: '/backoffice/registration/attendee/regular',
  slugRegistration: '/backoffice/registration/attendee',
  getRegularAttendee ({
    page = 0,
    itemsOnPage = 10,
    sort = 'id',
    name = 'Asc',
    filter
  } = {}) {
    const sorting = checkFilter(sort) ? `&sort.${sort}=${name}` : ''
    const firstName = checkFilter(filter.firstName) ? `&filter.FirstName.Like=${encodeURIComponent(filter.firstName)}` : ''
    const lastName = checkFilter(filter.lastName) ? `&filter.LastName.Like=${encodeURIComponent(filter.lastName)}` : ''
    const company = checkFilter(filter.company) ? `&filter.Company.Like=${encodeURIComponent(filter.company)}` : ''
    const email = checkFilter(filter.email) ? `&filter.Email.Like=${encodeURIComponent(filter.email)}` : ''
    const boothNumber = checkFilter(filter.boothNumber) ? `&filter.BoothNumber.Like=${encodeURIComponent(filter.boothNumber)}` : ''
    const selectedpackage = checkFilter(filter.selectedpackage) ? `&filter.Package.Id.equals=${filter.selectedpackage}` : ''
    const attendeeType = checkFilter(filter.attendeeType) ? `&filter.AttendeeTypeId.equals=${filter.attendeeType}` : ''
    const category = checkFilter(filter.category) ? `&filter.CategoryId.equals=${filter.category}` : ''
    const status = checkFilter(filter.attendeeStatus) ? `&filter.RegistrationStatus.equals=${filter.attendeeStatus}` : ''
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}${sorting}${firstName}${email}${boothNumber}${lastName}${company}${selectedpackage}${attendeeType}${category}${status}`
  },
  fetchRegularInvitations ({ page = 0, itemsOnPage = 9999, email } = {}) {
    // TODO need add regular expression FE-2757
    const emailFilter = email ? `&filter.Email.Like=/${encodeURIComponent(email)}/i` : ''
    return `${this.slug}/invited?page=${page}&itemsOnPage=${itemsOnPage}${emailFilter}`
  },
  setApproveStatus (id) {
    return `${this.slug}/approve?id=${id}`
  },
  editRegularAttendee () {
    return `${this.slug}`
  },
  recieveFullDataAboutAttendee (id) {
    return `${this.slugRegistration}/generic?filter.Id.Equals=${id}`
  },
  requestFeatureForm () {
    return `${this.slugRegistration}/generic/request-feature-form`
  },
  deleteAttendeeRecords (ids) {
    return `${this.slugRegistration}/generic/force?command.ids=${ids}`
  },
  changeIsInTop () {
    return `${this.slugRegistration}/generic/top`
  },
  getStatusesCount () {
    return `${this.slug}/counter`
  },
  getAllStatusesCount () {
    return `${this.slugRegistration}/generic/counter`
  },
  getRegularAttendeeStatusesCount () {
    return `${this.slugRegistration}/regular/counter`
  },
  getVipAttendeeStatusesCount () {
    return `${this.slugRegistration}/vip/counter`
  },
  sendRequestForPayment () {
    return `${this.slug}/request-for-payment`
  },
  sendRequestForPhoto () {
    return `${this.slug}/request-for-submit-image`
  },
  rejectAttendee (attendeeId) {
    return `${this.slugRegistration}/generic/reject?attendeeId=${attendeeId}`
  },
  getAttendeeVipInvitations ({ page = 0, itemsOnPage = 100, sort, filter: {receiverEmail, invitationName, invitationId}, name } = {}) {
    const sorting = checkFilter(sort) ? `&sort.${sort}=${name}` : ''
    let filterParams = receiverEmail ? `&filter.ReceiverEmail.Like=${receiverEmail}` : ''
    filterParams += invitationName ? `&filter.InvitationName.Like=${invitationName}` : ''
    filterParams += invitationId ? `&filter.Id.Equals=${invitationId}` : ''
    return `/backoffice/registration/attendee/vip/invitation?page=${page}&itemsOnPage=${itemsOnPage}${sorting}${filterParams}`
  },
  getAllAttendee ({ page = 0, itemsOnPage = 100, sort, filter, name } = {}) {
    const sorting = checkFilter(sort) ? `&sort.${sort}=${name}` : ''
    // TODO need add regular expression FE-2757
    const firstName = checkFilter(filter.firstName) ? `&filter.FirstName.Like=/${encodeURIComponent(filter.firstName)}/i` : ''
    const isVip = checkFilter(filter.isVip) ? `&filter.IsVip.Equals=${filter.isVip}` : ''
    const lastName = checkFilter(filter.lastName) ? `&filter.LastName.Like=/${encodeURIComponent(filter.lastName)}/i` : ''
    const company = checkFilter(filter.company) ? `&filter.Company.Like=/${encodeURIComponent(filter.company)}/i` : ''
    const email = checkFilter(filter.email) ? `&filter.Email.Like=/${encodeURIComponent(filter.email)}/i` : ''
    const boothNumber = checkFilter(filter.boothNumber) ? `&filter.BoothNumber.Like=${encodeURIComponent(filter.boothNumber)}` : ''
    // const selectedpackage = checkFilter(filter.selectedpackage) ? `&filter.Package.Id.equals=${filter.selectedpackage}` : ''
    const attendeeType = checkFilter(filter.attendeeType) ? `&filter.AttendeeTypeId.Equals=${filter.attendeeType}` : ''
    const category = checkFilter(filter.category) ? `&filter.CategoryId.Equals=${filter.category}` : ''
    // const status = checkFilter(filter.attendeeStatus) ? `&filter.RegistrationStatus.Equals=${filter.attendeeStatus}` : `&filter.RegistrationStatus.NotEquals=Trash`
    const status = checkFilter(filter.attendeeStatus) ? `&filter.RegistrationStatus.Equals=${filter.attendeeStatus}` : ``
    const id = checkFilter(filter.id) ? `&filter.Id.Equals=${filter.id}` : ''
    return `${this.slugRegistration}/generic?page=${page}&itemsOnPage=${itemsOnPage}${sorting}${firstName}${lastName}${company}${email}${boothNumber}${attendeeType}${category}${status}${id}${isVip}`
  },
  getExcelAttandee ({
    page = 0,
    itemsOnPage = 10,
    sort = 'id',
    name = 'Asc',
    filter
  } = {}) {
    const sorting = checkFilter(sort) ? `&sort.${sort}=${name}` : ''
    // TODO need add regular expression FE-2757
    const firstName = checkFilter(filter.firstName) ? `&filter.FirstName.Like=/${filter.firstName}/i` : ''
    const lastName = checkFilter(filter.lastName) ? `&filter.LastName.Like=/${filter.lastName}/i` : ''
    const company = checkFilter(filter.company) ? `&filter.Company.Like=/${filter.company}/i` : ''
    const email = checkFilter(filter.email) ? `&filter.Email.Like=/${filter.email}/i` : ''
    const boothNumber = checkFilter(filter.boothNumber) ? `&filter.BoothNumber.Like=/${filter.boothNumber}/i` : ''
    const selectedpackage = checkFilter(filter.selectedpackage) ? `&filter.Package.Id.equals=${filter.selectedpackage}` : ''
    const attendeeType = checkFilter(filter.attendeeType) ? `&filter.AttendeeTypeId.equals=${filter.attendeeType}` : ''
    const category = checkFilter(filter.category) ? `&filter.CategoryId.equals=${filter.category}` : ''
    const status = checkFilter(filter.attendeeStatus) ? `&filter.RegistrationStatus.equals=${filter.attendeeStatus}` : ''
    return `${this.slugRegistration}/generic/export/excel?page=${page}&itemsOnPage=${itemsOnPage}${sorting}${firstName}${email}${boothNumber}${lastName}${company}${selectedpackage}${attendeeType}${category}${status}`
  },
  getExcelAttandeeAll () {
    return `${this.slugRegistration}/generic/export/excel/all`
  },
  getCryptoTradersExcel () {
    return '/backoffice/registration/crypto-traders/export/excel'
  },
  changePackage () {
    return `${this.slug}/package`
  },
  fetchCryptoTraders ({ page = 0, itemsOnPage = 100, sort, filter, name } = {}) {
    const sorting = checkFilter(sort) ? `&sort.${sort}=${name}` : ''
    // TODO need add regular expression FE-2757
    const firstName = checkFilter(filter.firstName) ? `&filter.FirstName.Like=/${encodeURIComponent(filter.firstName)}/i` : ''
    const lastName = checkFilter(filter.lastName) ? `&filter.LastName.Like=/${encodeURIComponent(filter.lastName)}/i` : ''
    const email = checkFilter(filter.email) ? `&filter.Email.Like=/${encodeURIComponent(filter.email)}/i` : ''
    const status = checkFilter(filter.registrationStatus) ? `&filter.RegistrationStatus.Equals=${filter.registrationStatus}` : ''
    return `/backoffice/registration/crypto-traders?page=${page}&itemsOnPage=${itemsOnPage}${sorting}${status}${firstName}${lastName}${email}`
  },
  getCryptoPackages (attendeeEmail) {
    return `/public/registration/crypto-trader/packages?attendeeEmail=${attendeeEmail}`
  },
  updateTrader () {
    return '/backoffice/registration/crypto-traders'
  },
  updateTraderPackage () {
    return '/backoffice/registration/crypto-traders/package'
  },
  assignTraderPackage () {
    return '/backoffice/registration/crypto-traders/package/set'
  },
  updateSubscription () {
    return `${this.slugRegistration}/generic/subscriptions/mail-chimp`
  },
  getCryptotraderToken () {
    return '/backoffice/registration/crypto-traders/invitation'
  }
}

const attendeesMessages = {
  slug: '/backoffice/registration/messages/attendee-to-attendee',
  getAttendeesMessages ({ page = 0, itemsOnPage = 100, sort, filter, name } = {}) {
    const sorting = checkFilter(sort) ? `&sort.${sort}=${name}` : ''
    // TODO need add regular expression FE-2757
    const firstName = checkFilter(filter.firstName) ? `&filter.SenderFirstName.Like=/${filter.firstName}/i` : ''
    const lastName = checkFilter(filter.lastName) ? `&filter.SenderLastName.Like=/${filter.lastName}/i` : ''
    const company = checkFilter(filter.company) ? `&filter.SenderCompany.Like=/${filter.company}/i` : ''
    const jobTitle = checkFilter(filter.jobTitle) ? `&filter.SenderJobTitle.Like=/${filter.jobTitle}/i` : ''
    const receiverEmail = checkFilter(filter.receiverEmail) ? `&filter.ReceiverEmail.Like=/${filter.receiverEmail}/i` : ''
    const senderEmail = checkFilter(filter.senderEmail) ? `&filter.SenderEmail.Like=/${filter.senderEmail}/i` : ''
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}${sorting}${firstName}${jobTitle}${receiverEmail}${senderEmail}${lastName}${company}`
  }
}

const vipAttendee = {
  slug: '/backoffice/registration/attendee/vip',
  getVipAttendee ({ page = 0, itemsOnPage = 100, sort, filter, name } = {}) {
    const sorting = checkFilter(sort) ? `&sort.${sort}=${name}` : ''
    // TODO need add regular expression FE-2757
    const firstName = checkFilter(filter.firstName) ? `&filter.FirstName.Like=/${filter.firstName}/i` : ''
    const lastName = checkFilter(filter.lastName) ? `&filter.LastName.Like=/${filter.lastName}/i` : ''
    const company = checkFilter(filter.company) ? `&filter.Company.Like=/${filter.company}/i` : ''
    const email = checkFilter(filter.email) ? `&filter.Email.Like=/${filter.email}/i` : ''
    const boothNumber = checkFilter(filter.boothNumber) ? `&filter.BoothNumber.Like=/${filter.boothNumber}/i` : ''
    const selectedpackage = checkFilter(filter.selectedpackage) ? `&filter.Package.Id.equals=/${filter.selectedpackage}/i` : ''
    const attendeeType = checkFilter(filter.attendeeType) ? `&filter.VipAttendeeTypeId.equals=/${filter.attendeeType}/i` : ''
    const category = checkFilter(filter.category) ? `&filter.CategoryId.equals=/${filter.category}/i` : ''
    const status = checkFilter(filter.attendeeStatus) ? `&filter.RegistrationStatus.equals=/${filter.attendeeStatus}/i` : ''
    return `${this.slug}/attendees?page=${page}&itemsOnPage=${itemsOnPage}${sorting}${firstName}${email}${boothNumber}${lastName}${company}${selectedpackage}${attendeeType}${category}${status}`
  },
  editVipAttendee () {
    return `${this.slug}`
  },
  getStatusesCount () {
    return `${this.slug}/counter`
  },
  changePackage () {
    return `${this.slug}/package`
  }
}

const regularAttendeeSettings = {
  slug: '/backoffice/registration/attendee-settings',
  editRegularAttendeeSettings () {
    return `${this.slug}`
  }
}

const regularAttendeeType = {
  slug: '/backoffice/attendee-type',
  getRegularAttendeeType () {
    return `${this.slug}?page=0&itemsOnPage=99999`
  },
  addRegularAttendeeType () {
    return `${this.slug}`
  },
  editRegularAttendeeType () {
    return `${this.slug}`
  },
  deleteRegularAttendeeType (id) {
    return `${this.slug}/?id=${id}`
  }
}

const featuredAttendee = {
  updatePageSettings () {
    return '/backoffice/general-settings/featured-attendee-settings'
  }
}

const attendeeReminders = {
  slug: '/backoffice/registration/attendee-reminders/settings',
  getAttendeeReminders () {
    return `${this.slug}?page=0&itemsOnPage=99999`
  },
  editAttendeeReminders () {
    return `${this.slug}`
  },
  sendAttendeeReminders (attendeeId) {
    return `/backoffice/registration/attendee-reminders/send?attendeeId=${attendeeId}`
  },
  resetEmailTemplate (registrationStatus) {
    return `/backoffice/registration/attendee-reminders/settings/${registrationStatus}`
  }
}

const companies = {
  slug: '/backoffice',
  getCompaniesList ({ page = 0, itemsOnPage = 9999, sorter = {}, filter = {}, equalsStatus = {}, showOnlyApprove = true, fetchAll = false }) {
    const sortingParams = checkFilter(sorter.sortBy) ? `&sort.${sorter.direction}=${sorter.sortBy}` : ''
    // TODO need add regular expression FE-2757
    const filterParams = checkFilter(filter.searchValue) ? `&filter.${filter.filterBy}.Like=/${filter.searchValue}/i` : ''
    const equals = checkFilter(equalsStatus.searchWord) ? `&filter.${equalsStatus.filterBy}.equals=${equalsStatus.searchWord}` : ''
    const approved = checkFilter(showOnlyApprove) ? 'filter.BlitzInvitation.Status.equals=Approved' : ''
    if (fetchAll) {
      return `${this.slug}/blitz?itemsOnPage=${itemsOnPage}&${approved}&sort.Asc=Id`
    }
    return `${this.slug}/blitz?page=${page}&itemsOnPage=${itemsOnPage}${sortingParams}${filterParams}${equals}&${approved}`
  },
  update () {
    return `${this.slug}/blitz/approved`
  }
}

const speakers = {
  slug: '/backoffice/speakers',
  getSpeakersList ({ filterSpeaker } = {}) {
    if (checkFilter(filterSpeaker)) {
      // TODO need add regular expression FE-2757
      const filter = Object.keys(filterSpeaker).map(val => filterSpeaker[val] ? `&filter.${val[0].toUpperCase() + val.slice(1)}.Like=/${encodeURIComponent(filterSpeaker[val])}/i` : '')
      return `${this.slug}?page=0&itemsOnPage=99999${filter.join('')}`
    } else {
      return `${this.slug}?page=0&itemsOnPage=99999`
    }
  },
  deleteSpeaker (ids) {
    return `${this.slug}?statusModel.speakersIds=${ids[0]}`
  },
  draftSpeaker () {
    return `${this.slug}/draft`
  },
  publishSpeaker () {
    return `${this.slug}/publish`
  },
  createEditSpeaker () {
    return `${this.slug}`
  },
  putSpeakersSettings () {
    return '/backoffice/speakers/settings'
  },
  patchOrdersSpeaker () {
    return this.slug
  },
  setSpeakersVisibleHomepage () {
    return `${this.slug}/home-page`
  },
  fetchSpeakersVisibleHomepage () {
    return '/public/speakers/home-page'
  }
}

const speakersSettings = {
  updateSettings () {
    return '/backoffice/speakers/settings'
  }
}

const statusDisplayNames = {
  slug: '/backoffice/registration/status/display-name',
  getStatusDisplayNames () {
    return this.slug
  },
  updateStatusDisplayNames () {
    return this.slug
  }
}

const tag = {
  slug: '/backoffice/registration/tag',
  getTag () {
    return '/public/tag?page=0&itemsOnPage=99999'
  },
  getTagByType (type) {
    return `/public/tag/${type}?page=0&itemsOnPage=99999`
  },
  postTag () {
    return `${this.slug}`
  },
  putTag () {
    return `${this.slug}`
  },
  deleteTag (id) {
    return `${this.slug}/?id=${id}`
  },
  putTagType () {
    return `${this.slug}/type`
  }
}

const nominations = {
  categoriesSlug: '/backoffice/nominations/categories',
  getCategories ({ page = 0, itemsOnPage = 9999, filter, type, parentCategory, sort, name }) {
    const sorting = checkFilter(sort) && name ? `&sort.${sort}=${name}` : ''
    // TODO need add regular expression FE-2757
    const filtration = checkFilter(filter) ? `&filter.Title.Like=/${encodeURIComponent(filter)}/i` : ''
    const filtrationType = checkFilter(type) ? `&filter.AwardsType.Equals=${type}` : ''
    const filtrationParentCategory = checkFilter(parentCategory) ? `&filter.ParentCategory.Equals=${parentCategory}` : ''
    return `${this.categoriesSlug}?page=${page}&itemsOnPage=${itemsOnPage}${filtration}${filtrationType}${filtrationParentCategory}${sorting}`
  },
  putCategory () {
    return `${this.categoriesSlug}`
  },
  postCategory () {
    return `${this.categoriesSlug}`
  },
  deleteCategory (id) {
    return `${this.categoriesSlug}?command.id=${id}`
  }
}

const emailTemplates = {
  slug: '/backoffice/general-settings/email-templates',
  getEmailTemplates (obj = {}) {
    if (Object.keys(obj).length > 0) {
      const getFilter = function (keyName, filter) {
        const filterValue = filter ? obj[filter][keyName] : obj[keyName]
        const filterMethod = filter || 'Like'
        const filterWay = keyName[0].toUpperCase() + keyName.slice(1)
        return filterValue ? `&filter.${filterWay}.${filterMethod}=${filterValue}` : ''
      }
      const checkObj = (obj, filter) => Object.keys(obj).map(keyName => {
        return keyName === 'Equals' ? checkObj(obj['Equals'], 'Equals') : getFilter(keyName, filter)
      })
      const filterArray = checkObj(obj)
      return `${this.slug}?page=0&itemsOnPage=99999${filterArray.join('')}`
    } else {
      return `${this.slug}?page=0&itemsOnPage=999999`
    }
  },
  postEmailTemplate () {
    return `${this.slug}`
  },
  putEmailTemplate () {
    return `${this.slug}`
  },
  deleteEmailTemplate (key, culture) {
    return `${this.slug}/${key}/${culture}`
  },
  editEmailTemplate () {
    return `${this.slug}/layout`
  }
}

const nominees = {
  slug: '/backoffice/nominee',
  getNomineesList ({ page = 0, itemsOnPage = 9999, filter, sort, name }) {
    const sorting = checkFilter(sort) && name ? `&sort.${sort}=${name}` : ''
    // TODO need add regular expression FE-2757
    const filtrationName = checkFilter(filter.name) ? `&filter.Name.Like=/${encodeURIComponent(filter.name)}/i` : ''
    const filtrationType = checkFilter(filter.type) ? `&filter.AwardsType.Equals=${filter.type}` : ''
    const filtrationAttendeeEmail = checkFilter(filter.submittedAttendeeEmail) ? `&filter.SubmittedAttendeeEmail.Like=/${encodeURIComponent(filter.submittedAttendeeEmail)}/i` : ''
    const filtrationCategoryId = checkFilter(filter.categoryId) ? `&filter.CategoriesIds.Any=${filter.categoryId}` : ''
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}${filtrationName}${filtrationAttendeeEmail}${filtrationType}${filtrationCategoryId}${sorting}`
  },
  getNomineesListWithStatusWining () {
    return `${this.slug}?itemsOnPage=999&filter.VotingStep.Equals=Wining`
  },
  addNominee () {
    return `${this.slug}`
  },
  editNominee () {
    return `${this.slug}`
  },
  removeNominee (id) {
    return `${this.slug}/?command.ids=${id}`
  },
  getVotingExcel () {
    return `${this.slug}/export-excel`
  },
  getVotingExcelIndividual () {
    return `${this.slug}/individual/export-excel`
  },
  approveNominee (id) {
    return `${this.slug}/approve/${id}`
  },
  exportNomineesExcel () {
    return `${this.slug}/nominees-export-excel`
  }
}

const generalSettings = {
  slugGlobalSettings: '/backoffice/general-settings',
  updateSetting () {
    return `${this.slugGlobalSettings}/global`
  },
  updateVatIdSettings () {
    return '/backoffice/general-settings/payment/vat-id'
    // return `${this.slugGlobalSettings}/vat-id-setting`
  },
  files ({ itemsOnPage } = { itemsOnPage: 99999 }) {
    return `/backoffice/general-settings/files?itemsOnPage=${itemsOnPage}`
  },
  filesStorage: {
    slug: '/backoffice/general-settings/files-storage',
    upload () {
      return `${this.slug}/upload`
    }
  },
  getSettings () {
    return '/public/settings'
  },
  getDomains ({page = 0, itemsOnPage = 9999, domain}) {
    if (domain) {
      // TODO need add regular expression FE-2757
      const filtrationName = `filter.Domain.Like=/${domain}/i`
      return `/backoffice/master/forbidden-email-domains?page=${page}&itemsOnPage=${itemsOnPage}&${filtrationName}`
    } else {
      return `/backoffice/master/forbidden-email-domains?page=${page}&itemsOnPage=${itemsOnPage}`
    }
  },
  postDomain () {
    return '/backoffice/master/forbidden-email-domains'
  },
  postPrefix () {
    return '/backoffice/master/forbidden-email-domains/prefix-name'
  },
  deleteDomain (id) {
    return `/backoffice/master/forbidden-email-domains/${id}`
  },
  putAttendeePassword () {
    return '/backoffice/general-settings/global/attendee-password'
  }
}

const promoCodes = {
  slug: '/backoffice/registration/promo-codes',
  getPromoCodesList ({ page = 0, itemsOnPage = 9999, filter, sort, name }) {
    const sorting = checkFilter(sort) ? `&sort.${sort}=${name}` : ''
    // TODO need add regular expression FE-2757
    const filterName = checkFilter(pathOr('', ['filterName'], filter)) ? `&filter.Name.Like=/${encodeURIComponent(filter.filterName)}/i` : ''
    const filterStatus = checkFilter(pathOr('', ['filterStatus'], filter)) ? `&filter.Status.equals=${encodeURIComponent(filter.filterStatus)}` : ''
    const filterUseCount = checkFilter(pathOr('', ['filterUseCount'], filter)) ? `&filter.UseCount.equals=${encodeURIComponent(filter.filterUseCount)}` : ''
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}${filterName}${filterStatus}${filterUseCount}${sorting}`
  },
  addPromoCode () {
    return `${this.slug}`
  },
  editPromoCode () {
    return `${this.slug}`
  },
  updateSettings () {
    return `${this.slug}/settings`
  }
}

const staticContent = {
  slug: '/backoffice/general-settings/static-pages/content',
  getContent ({ itemsOnPage = 999, page = 1 } = {}) {
    return `/public/static-pages/content?itemsOnPage=${itemsOnPage}`
  },
  deleteContent (ids) {
    const reduceIds = (acc, el, index) => {
      const firstAmpersand = index === 0 ? '' : '&'
      acc += `${firstAmpersand}command.ids=${el}`
      return acc
    }
    const queryStr = Array.isArray(ids)
      ? ids.reduce(reduceIds, '')
      : `command.ids=${ids}`
    return `${this.slug}?${queryStr}`
  },
  putContent () {
    return `${this.slug}`
  },
  postContent () {
    return `${this.slug}`
  }
}

const excel = {
  slug: '/backoffice',
  downloadExcel (field) {
    const url = {
      sponsors: `${this.slug}/media-partners/export/excel`,
      speakers: `${this.slug}/speakers/export/excel`,
      blitz: `${this.slug}/blitz/export-blitzes`,
      listing: `${this.slug}/listing/export/excel`
    }
    return url[field]
  }
}

const testimonials = {
  slug: '/public/testimonials',
  settingsSlug: '/backoffice/testimonials',
  getAll () {
    return `${this.slug}`
  },
  deleteTestimonial (id) {
    return `${this.settingsSlug}?command.id=${id}`
  },
  createTestimonial () {
    return `${this.settingsSlug}`
  },
  updateTestimonial () {
    return `${this.settingsSlug}`
  }
}

const lastYearTestimonials = {
  slug: '/backoffice/testimonials/last-year',
  slugTypes: '/backoffice/testimonials/types',
  getLastYearTestimonials ({ page = 0, itemsOnPage = 9999, filter, sort, name }) {
    // TODO need add regular expression FE-2757
    const filtringByName = checkFilter(filter.searchByName) ? `&filter.EmployeeName.Like=/${encodeURIComponent(filter.searchByName)}/i` : ''
    const filtringByCompany = checkFilter(filter.searchByCompany) ? `&filter.CompanyName.Like=/${encodeURIComponent(filter.searchByCompany)}/i` : ''
    const filtringByStatus = checkFilter(filter.filterByStatus) ? `&filter.Status.Equals=${filter.filterByStatus}` : ''
    const filtringByType = checkFilter(filter.filterByType) ? `&filter.TestimonialTypeId.Equals=${filter.filterByType}` : ''
    const sorting = checkFilter(sort) ? `&sort.${sort}=${name}` : ''
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}${sorting}${filtringByType}${filtringByName}${filtringByCompany}${filtringByStatus}`
  },
  gePublictLastYearTestimonials () {
    return '/public/testimonials/last-year?itemsOnPage=9999'
  },
  getLastYearTestimonialsByType (typeId) {
    return `${this.slug}?filter.TestimonialTypeId.equals=${typeId}&itemsOnPage=9999`
  },
  addLastYearTestimonial () {
    return this.slug
  },
  updateLastYearTestimonial () {
    return this.slug
  },
  deleteLastYearTestimonial (id) {
    return `${this.slug}?command.id=${id}`
  },
  changeStatusLYTestimonial () {
    return `${this.slug}/status`
  },
  getTestimonialTypes () {
    return '/public/testimonials/types'
  },
  addTestimonialTypes () {
    return `${this.slugTypes}`
  },
  updateTestimonialTypes () {
    return `${this.slugTypes}`
  },
  removeTestimonialType ({ id, replacementTypeId }) {
    const replacementType = checkFilter(replacementTypeId) ? `&command.replacementTypeId=${replacementTypeId}` : ''
    return `${this.slugTypes}?command.id=${id}${replacementType}`
  },
  updateDisplaySettingsTestimonials () {
    return '/backoffice/general-settings/last-year-testimonials/page-settings'
  }
}

const cookiesAlert = {
  updateSettings () {
    return '/backoffice/general-settings/cookie-alert'
  }
}

const google = {
  // AUTHORIZATION
  authSlug: '/backoffice/general-settings/google-authorization',
  googleSheetsSlug: '/backoffice/general-settings/google-sheets-settings',
  fetchGoogleAuthLink () {
    return `${this.authSlug}/link`
  },
  fetchGoogleAuthCredentials () {
    return `${this.authSlug}`
  },
  authorize (authCode) {
    return `${this.authSlug}/authorize?code=${authCode}`
  },

  // SETTINGS
  updateSettings () {
    return this.googleSheetsSlug
  },
  pixel () {
    return '/backoffice/general-settings/google-pixel-settings'
  }
}

const photoGallery = {
  slug: '/backoffice/general-settings/photo-gallery',
  getPhotoGalleriesList () {
    return `${this.slug}`
  },
  createPhotoGallery () {
    return `${this.slug}`
  },
  updatePhotoGallery () {
    return `${this.slug}`
  },
  deletePhotoGallery (id) {
    return `${this.slug}/${id}`
  }
}

const blockAttendeeSettings = {
  updateSettings () {
    return '/backoffice/general-settings/global/block-attendee'
  },
  unblockAttendee (attendeeId) {
    return `/backoffice/registration/attendee/generic/unblock?attendeeId=${attendeeId}`
  }
}

const awardsSettings = {
  slug: 'backoffice/awards/',
  votingSlug: 'backoffice/voting/',
  votingSlugIndividual: 'backoffice/voting/individual/',
  fetchVotingVoters () {
    return `${this.votingSlug}voters`
  },
  fetchVotingStepOne (name) {
    // TODO need add regular expression FE-2757
    const filtrationName = name ? `&filter.Nominees.Name.Like=/${encodeURIComponent(name)}/i` : ''
    return `${this.votingSlug}step-one?page=0&itemsOnPage=9999${filtrationName}`
  },
  fetchVotingStepTwo (name) {
    // TODO need add regular expression FE-2757
    const filtrationName = name ? `&filter.Nominees.Name.Like=/${encodeURIComponent(name)}/i` : ''
    return `${this.votingSlug}step-two?page=0&itemsOnPage=9999${filtrationName}`
  },
  fetchStepOneStatistics () {
    return `${this.votingSlug}step-one/total`
  },
  fetchStepTwoStatistics () {
    return `${this.votingSlug}step-two/total`
  },
  fetchVotingStepOneIndividual (name) {
    const filtrationName = name ? `&filter.Nominees.Name.Like=/${encodeURIComponent(name)}/i` : ''
    return `${this.votingSlugIndividual}step-one?page=0&itemsOnPage=9999${filtrationName}`
  },
  fetchVotingStepTwoIndividual (name) {
    const filtrationName = name ? `&filter.Nominees.Name.Like=/${encodeURIComponent(name)}/i` : ''
    return `${this.votingSlugIndividual}step-two?page=0&itemsOnPage=9999${filtrationName}`
  },
  fetchStepOneStatisticsIndividual () {
    return `${this.votingSlugIndividual}step-one/total`
  },
  fetchStepTwoStatisticsIndividual () {
    return `${this.votingSlugIndividual}step-two/total`
  },
  updateGeneralSettings () {
    return `${this.slug}settings/upsert`
  },
  updateIndividualSettings () {
    return `${this.slug}settings/individual/upsert`
  },
  updateWelcomeSettings () {
    return `${this.slug}settings/welcome/upsert`
  },
  updateStepOneSettings () {
    return `${this.slug}settings/step-one`
  },
  updateStepTwoSettings () {
    return `${this.slug}settings/step-two`
  },
  updateStepOneIndividualSettings () {
    return `${this.slug}settings/individual/step-one`
  },
  updateStepTwoIndividualSettings () {
    return `${this.slug}settings/individual/step-two`
  },
  updateSettingsWinners () {
    return `${this.slug}settings/winners/upsert`
  },
  updateSettingsIndividualWinners () {
    return `${this.slug}settings/individual/winners/upsert`
  }
}

const awardsReminders = {
  slug: 'backoffice/award-reminders/',
  fetchReminders () {
    return `${this.slug}`
  },
  sendReminder () {
    return `${this.slug}send-reminder`
  }
}

const lastYearAttendees = {
  fetchAttendees () {
    return '/public/attendees/last-year'
  },
  uploadAttendees () {
    return '/backoffice/registration/last-year-attendee'
  }
}

const listing = {
  slug: '/backoffice/listing',
  getListing ({ page = 0, itemsOnPage = 10, filterCompany = {}, filterEmail = {}, filterName = {}, sorter = {}, equalsStatus = {} } = {}) {
    // TODO need add regular expression FE-2757
    const filtrationCompany = checkFilter(filterCompany.searchValue) ? `&filter.${filterCompany.filterBy}.Like=/${encodeURIComponent(filterCompany.searchValue)}/i` : ''
    const filtrationEmail = checkFilter(filterEmail.searchValue) ? `&filter.${filterEmail.filterBy}.Like=/${encodeURIComponent(filterEmail.searchValue)}/i` : ''
    const filtrationName = checkFilter(filterName.searchValue) ? `&filter.${filterName.filterBy}.Like=/${encodeURIComponent(filterName.searchValue)}/i` : ''
    const sorting = checkFilter(sorter.sortBy) ? `&sort.${sorter.direction}=${sorter.sortBy}` : ''
    const status = checkFilter(equalsStatus.status) ? `&filter.${equalsStatus.filterBy}.equals=${equalsStatus.status}` : ''
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}${filtrationCompany}${filtrationName}${filtrationEmail}${sorting}${status}`
  },
  resetStatusToNotYet (id) {
    return `${this.slug}/set-not-yet-status?id=${id}`
  },
  counterListing () {
    return `${this.slug}/counter`
  },
  createListing () {
    return `${this.slug}`
  },
  updateListing () {
    return `${this.slug}/approved`
  },
  updatePendingListing () {
    return `${this.slug}`
  },
  updateApproveListing (id) {
    return `${this.slug}/approve?submitId=${id}`
  },
  remindListing () {
    return `${this.slug}/remind`
  },
  deleteListing (id) {
    return `${this.slug}/delete?id=${id}`
  },
  exportListingPDF () {
    return `${this.slug}/export/pdf/zip`
  }
}

const listingInvitation = {
  createPreviewListingPDF () {
    return '/public/listing/submit/preview'
  }
}

const killerFeatureDisplaySettings = {
  updateKillerFeatureDisplaySettings () {
    return `/backoffice/registration/killer-feature/display-settings`
  }
}

const generalKillerFeatureSettings = {
  updateSettings () {
    return '/backoffice/registration/killer-feature/display-settings'
  }
}

const voucherPage = {
  putVoucherPage () {
    return '/backoffice/general-settings/vaucher-page-settings'
  }
}

const badge = {
  slug: '/backoffice/registration/badge/export-zip',
  getBadges (password) {
    return `${this.slug}?password=${password}`
  }
}

const registrationVipInvitation = {
  slug: '/backoffice/registration/attendee/vip/invitation',
  fetchInvitations ({ itemsOnPage = 999, page = 0 } = {}) {
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}`
  },
  fetchBatchedInvitations () {
    return '/backoffice/registration/attendee/vip/getVipInvitations'
  },
  fetchInvitedAttendeesByCode (code) {
    return '/backoffice/invite-management/invitation/attendees?code=' + code
  },
  saveBatchedInvitations () {
    return `${this.slug}/update`
  },
  sendBatchedInvitations () {
    return `${this.slug}/send`
  },
  fetchInvitationPackage (id) {
    return `${this.slug}/package/${id}`
  },
  updateInvitation () {
    return `${this.slug}`
  },
  updateInvitationPackage () {
    return `${this.slug}/package`
  },
  deleteInviation (id) {
    return `${this.slug}?command.id=${id}`
  },
  enableInvitation () {
    return `${this.slug}/enable`
  },
  disableInvitation () {
    return `${this.slug}/disable`
  },
  resendInvitation () {
    return '/backoffice/registration/attendee/vip/invitation/resend'
  },
  deleteBatchedInvitation (id) {
    return `/backoffice/registration/attendee/vip/invitation?id=${id}`
  },
  reSendBatchedInvitations () {
    return `${this.slug}/resend/multiple`
  }
}

const emailNotificationSettings = {
  slug: '/backoffice/general-settings/email-notifications',
  getAdminNotificationSettings () {
    return `${this.slug}/admin?itemsOnPage=999`
  },
  updateAdminNotificationSettings () {
    return `${this.slug}/admin`
  },
  getAttendeeNotificationSettings () {
    return `${this.slug}/attendee?itemsOnPage=999`
  },
  updateAttendeeNotificationSettings () {
    return `${this.slug}/attendee`
  }
}

const massAttendeeActions = {
  slug: '/backoffice/registration/attendee/generic/mass-actions',
  resendConfirmationEmail () {
    return `${this.slug}/resend-confirmation`
  },
  requestForPhoto () {
    return `${this.slug}/request-for-submit-image`
  },
  setActivitiesRegular () {
    return `${this.slug}/regular/set-activities`
  },
  setActivitiesVip () {
    return `${this.slug}/vip/set-activities`
  },
  unsetActivitiesRegular () {
    return `${this.slug}/regular/unset-activities`
  },
  unsetActivitiesVip () {
    return `${this.slug}/vip/unset-activities`
  },
  setStatus () {
    return `${this.slug}/set-status`
  },
  RequestForPayment () {
    return `${this.slug}/request-for-payment`
  },
  addToBlacklist () {
    return `${this.slug}/set-blacklisted`
  },
  setInfo () {
    return `${this.slug}/set-info`
  },
  setMessageLimitRegulars () {
    return `${this.slug}/regular/set-message-limit`
  },
  setMessageLimitVips () {
    return `${this.slug}/vip/set-message-limit`
  },
  setIndustry () {
    return `${this.slug}/set-industry`
  },
  setTypeRegulars () {
    return `${this.slug}/regular/set-type`
  },
  setTypeVips () {
    return `${this.slug}/vip/set-type`
  },
  killerFeatureTagSearchHide () {
    return `${this.slug}/killer-feature/search-status`
  },
  hideEnvelope () {
    return `${this.slug}/hide-envelope`
  },
  patchAttendeeViaExcel () {
    return `${this.slug}/update-attendees-via-excel`
  },
  patchNewAttendeeViaExcel () {
    return `${this.slug}/import-attendees-via-excel`
  },
  sendReminders () {
    return `${this.slug}/send-reminders`
  }
}

const invoices = {
  slug: '/backoffice/registration/invoices',
  get ({
    page = 0,
    itemsOnPage = 10,
    filterFirstName = {},
    filterLastName = {},
    filterEmail = {},
    filterCompany = {},
    filterPaymentProviderTransactionId = {},
    equalsTransactionType = {},
    equalsPaymentProvider = {},
    sorter = {}
  } = {}) {
    // TODO need add regular expression FE-2757
    const filtrationFirstName = checkFilter(filterFirstName.searchValue) ? `&filter.${filterFirstName.filterBy}.Like=/${encodeURIComponent(filterFirstName.searchValue)}/i` : ''
    const filtrationLastName = checkFilter(filterLastName.searchValue) ? `&filter.${filterLastName.filterBy}.Like=/${encodeURIComponent(filterLastName.searchValue)}/i` : ''
    const filtrationEmail = checkFilter(filterEmail.searchValue) ? `&filter.${filterEmail.filterBy}.Like=/${encodeURIComponent(filterEmail.searchValue)}/i` : ''
    const filtrationCompany = checkFilter(filterCompany.searchValue) ? `&filter.${filterCompany.filterBy}.Like=/${encodeURIComponent(filterCompany.searchValue)}/i` : ''
    const filtrationPaymentProviderTransactionId = checkFilter(filterPaymentProviderTransactionId.searchValue) ? `&filter.${filterPaymentProviderTransactionId.filterBy}.Like=/${encodeURIComponent(filterPaymentProviderTransactionId.searchValue)}/i` : ''
    const sorting = checkFilter(sorter.sortByValue) ? `&sort.${sorter.direction}=${sorter.sortByValue}` : ''
    const transactionType = checkFilter(equalsTransactionType.status) ? `&filter.${equalsTransactionType.filterBy}.equals=${equalsTransactionType.status}` : ''
    const paymentProvider = checkFilter(equalsPaymentProvider.status) ? `&filter.${equalsPaymentProvider.filterBy}.equals=${equalsPaymentProvider.status}` : ''
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}${filtrationFirstName}${filtrationLastName}${filtrationEmail}${filtrationCompany}${filtrationPaymentProviderTransactionId}${sorting}${transactionType}${paymentProvider}`
  },
  downloadPDF (paymentProviderTransactionId) {
    return `${this.slug}/download-single-pdf?paymentProviderTransactionId=${paymentProviderTransactionId}`
  },
  downloadExcel () {
    return `${this.slug}/export-zip`
  },
  downloadIPNpdf (paymentProviderTransactionId) {
    return `${this.slug}/ipn/download-single-pdf?paymentProviderTransactionId=${paymentProviderTransactionId}`
  },
  resend () {
    return `${this.slug}/resend`
  },
  resendIPN (paymentProviderTransactionId) {
    return `${this.slug}/ipn/resend?paymentProviderTransactionId=${paymentProviderTransactionId}`
  }
}

const emailTemplatesSettings = {
  slug: '/backoffice/general-settings/email-templates-mappings',
  updateIPNSendingTemplate () {
    return `${this.slug}/ipn-sending`
  },
  updateIPNRefundSendingTemplate () {
    return `${this.slug}/ipn-refund-sending`
  },
  updateAttendeeConfirmationTemplate () {
    return `${this.slug}/attendee-confirmation`
  },
  updateAttendeeMessageTemplate () {
    return `${this.slug}/attendee-message`
  },
  updateAttendeeToAgendaAdminMessageTemplate () {
    return `${this.slug}/attendee-to-agenda-session-admin-message`
  },
  updateAttendeeToSpeakerMessageTemplate () {
    return `${this.slug}/attendee-to-speaker-message`
  },
  updateAttendeeToParticipantMessageTemplate () {
    return `${this.slug}/attendee-to-blitz-participant-message`
  },
  updateBlitzSubmittedTemplate () {
    return `${this.slug}/blitz-submitted`
  },
  updateContactUsTemplate () {
    return `${this.slug}/contact-us`
  },
  updateEnableVideoRequestTemplate () {
    return `${this.slug}/enable-video-request`
  },
  updateInvoiceRefundSendingTemplate () {
    return `${this.slug}/invoice-refund-sending`
  },
  updateInvoiceSendingTemplate () {
    return `${this.slug}/invoice-sending`
  },
  updateListingSubmittedTemplate () {
    return `${this.slug}/listing-submitted`
  },
  updateResetPasswordTemplate () {
    return `${this.slug}/reset-password`
  },
  updateAdminSetPasswordTemplate () {
    return `${this.slug}/user-reset-password`
  }
}

const attendeeVoteReminders = {
  slug: '/backoffice/registration/attendee-vote-reminders/settings',
  fetchReminders () {
    return `${this.slug}`
  },
  updateReminder () {
    return `${this.slug}`
  },
  deleteReminder (status) {
    return `${this.slug}/${status}`
  }
}

const messages = {
  slug: '/backoffice/general-settings/messages',
  getMessages ({ page = 0, itemsOnPage = 10, sorter = {}, equalsMessageType = {} }) {
    const sorting = checkFilter(sorter.sortBy) ? `&sort.${sorter.direction}=${sorter.sortBy}` : ''
    const equalsMessage = checkFilter(equalsMessageType.status) ? `&filter.${equalsMessageType.filterBy}.equals=${equalsMessageType.status}` : ''
    return `${this.slug}?page=${page}&itemsOnPage=${itemsOnPage}${equalsMessage}${sorting}`
  }
}

const sendGrid = {
  fetchTemplates () {
    return '/backoffice/general-settings/send-grid'
  }
}

const mailChimp = {
  slug: '/backoffice/general-settings/mail-chimp',
  fetchNewsList () {
    return `${this.slug}?itemsOnPage=9999`
  },
  updateNewsList () {
    return `${this.slug}`
  },
  getSegmentsList (id) {
    return `${this.slug}/${id}/segments`
  }
}

const generalStyles = {
  slug: '/backoffice/general-settings/global-style-settings/upsert',
  updateGeneralStyles () {
    return this.slug
  }
}

const venue = {
  slug: '/backoffice/general-settings/venue-page-settings',
  updateBookHotelSettings () {
    return `${this.slug}`
  }
}

export const api = {
  signalR,
  baseUrl,
  users,
  auth,
  galleries,
  form,
  invitations,
  formConstuctor,
  categories,
  companies,
  packages,
  predefinedActivity,
  blitz,
  agenda,
  sponsors,
  regularAttendeeSettings,
  regularAttendeeType,
  attendeeReminders,
  statusDisplayNames,
  vipInvitation,
  vipAttendeeType,
  speakers,
  speakersSettings,
  tag,
  attendee,
  vipAttendee,
  invitation,
  nominations,
  nominees,
  emailTemplates,
  promoCodes,
  generalSettings,
  staticContent,
  testimonials,
  featuredAttendee,
  excel,
  photoGallery,
  video,
  videosDisplay,
  banner,
  cookiesAlert,
  lastYearTestimonials,
  google,
  lastYearAttendees,
  blockAttendeeSettings,
  awardsSettings,
  awardsReminders,
  invitationSingle,
  invitationMultiply,
  listing,
  listingInvitation,
  killerFeatureDisplaySettings,
  generalKillerFeatureSettings,
  voucherPage,
  badge,
  blockedIps,
  registrationVipInvitation,
  publicSettings,
  emailNotificationSettings,
  massAttendeeActions,
  attendeesMessages,
  invoices,
  emailTemplatesSettings,
  attendeeVoteReminders,
  sendGrid,
  messages,
  hideOrShowEnvelop,
  blackList,
  mailChimp,
  mapLocations,
  generalStyles,
  venue
}



// WEBPACK FOOTER //
// ./src/modules/api/index.js